import { DataDictionaryView } from '@types';
import { getDataDictionaryView } from './getDataDictionaryView';
import { ResultsCollection } from '@sonar-software/xuder';
import { SonarObjectType } from '@sonar-software/sonar-ts-types';

import { ApiEndpointHandler, createGetEndpoint } from '@sonar-software/sonar-http';

export type GetDataDictionaryViewsRequest = {
	applicationId: number | undefined;
	complianceCategory: number | undefined;
	sonarObjectId: number | undefined;
	sonarObjectType: SonarObjectType | undefined;
	tagId: number | undefined;
};

export type GetDataDictionaryViewsResponse = Array<DataDictionaryView>;

const getDataDictionaryViewsForSonarObject = createGetEndpoint<
	GetDataDictionaryViewsRequest,
	GetDataDictionaryViewsResponse
>((request) => `/api/v3/data-dictionary/views/fields/${request.sonarObjectId}`);

const getDataDictionaryViewsForType = createGetEndpoint<GetDataDictionaryViewsRequest, GetDataDictionaryViewsResponse>(
	(request) => `/api/v3/data-dictionary/views/sonar-objects/types/${request.sonarObjectType}`
);

const getDataDictionaryViewsForApp = createGetEndpoint<GetDataDictionaryViewsRequest, GetDataDictionaryViewsResponse>(
	(request) => `/api/v3/data-dictionary/views/applications/${request.applicationId}`
);

const getDataDictionaryViewsForTag = createGetEndpoint<GetDataDictionaryViewsRequest, GetDataDictionaryViewsResponse>(
	(request) => `/api/v3/data-dictionary/views/tags/${request.tagId}`
);

const getDataDictionaryViewsForComplianceCategory = createGetEndpoint<
	GetDataDictionaryViewsRequest,
	GetDataDictionaryViewsResponse
>((request) => `/api/v3/data-dictionary/views/compliance-categories/${request.complianceCategory}`);

export const getDataDictionaryViews: ApiEndpointHandler<
	GetDataDictionaryViewsRequest,
	ResultsCollection<DataDictionaryView>
> = async(request) => {
	let response;

	if (request.sonarObjectId) {
		response = await getDataDictionaryViewsForSonarObject(request);
	} else if (request.sonarObjectType) {
		response = await getDataDictionaryViewsForType(request);
	} else if (request.applicationId) {
		response = await getDataDictionaryViewsForApp(request);
	} else if (request.tagId) {
		response = await getDataDictionaryViewsForTag(request);
	} else {
		response = await getDataDictionaryViewsForComplianceCategory(request);
	}

	if (!response.data) {
		return {
			...response,
			data: undefined
		};
	}

	const views = response.data;

	await Promise.all(
		views.map((view, viewIndex) =>
			getDataDictionaryView({ id: view.id }).then((viewDetailsResponse) => {
				const viewDetails = viewDetailsResponse.data;

				if (!viewDetails) {
					return;
				}

				views[ viewIndex ] = {
					...view,
					columns: viewDetails.columns,
					filter: viewDetails.filter,
					ordering: viewDetails.ordering,
					sharedKey: viewDetails.sharedKey
				};
			}))
	);

	return {
		...response,
		data: { results: response.data }
	};
};
